import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    Button,
    DataTable,
    Grid,
    Heading,
    Notification,
    Page,
    PageContent,
    Pagination,
    Text,
    TextInput,
} from 'grommet';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import {
    API,
    FilterOptionSelect,
    NewMatchButton,
    AppConstants,
    ApiSearch,
    ApiOptionsSelect,
    getGradYears,
    formatEnumLabel,
} from '@enrich/shared-lib';
import { contactsQuery } from './SearchRequests';
import { ContactListButton } from './ContactListButton';

const graduationOptions = getGradYears().flatMap((year) => [
    { name: `Spring ${year}`, value: { year: year, semester: 'spring' } },
    { name: `Fall ${year}`, value: { year: year, semester: 'fall' } },
]);

export const SearchStudents = () => {
    const loaderData = useLoaderData() || {};
    const [buttonBusy, setButtonBusy] = useState(false);
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const defaultResultColumns = [
        {
            property: 'firstName',
            header: <Text>Name</Text>,
            render: (contact) => (
                <Anchor
                    href={`contacts/${contact.id}`}
                    label={`${contact.firstName} ${contact.lastName}`}
                />
            ),
            sortable: true,
        },
        {
            property: 'majors.data.attributes.name',
            header: <Text>Major</Text>,
            render: (contact) => {
                return contact.majors.data
                    .map((major) => major.attributes.name)
                    .sort((a, b) => a.localeCompare(b))
                    .join(', ');
            },
        },
        {
            property: 'graduationYear',
            header: <Text>Graduation</Text>,
            render: (contact) => {
                return `${contact.graduationSemester} ${contact.graduationYear}`;
            },
            sortable: true,
        },
        {
            property: 'school.data.attributes.name',
            header: <Text>School</Text>,
            sortable: true,
        },
        {
            property: 'email',
            header: <Text>Email</Text>,
            sortable: true,
        },
        {
            property: 'resumeStatus',
            header: <Text>Resume Status</Text>,
            render: (contact) =>
                formatEnumLabel(contact.resumeStatus) || 'N/A',
            sortable: true,
        },
        {
            property: 'communicationLevel',
            header: <Text>Communication Level</Text>,
            render: (contact) =>
                !contact.communicationLevel || contact.communicationLevel == 0
                    ? 'N/A'
                    : contact.communicationLevel,
            sortable: true,
        },
    ];

    const resumeStatusOptions = (loaderData.resumeStatuses ?? []).map(
        (status) => ({ ...status, label: formatEnumLabel(status.name) }),
    );

    const revalidator = useRevalidator();
    const [searchJob, setSearchJob] = useState();
    const [searchResults, setSearchResults] = useState([]);
    const [searchResultsTotal, setSearchResultsTotal] = useState();
    const [searchFilters, setSearchFilters] = useState({});
    const [resultsPage, setResultsPage] = useState();
    const [selectedContactIds, setSelectedContactIds] = useState([]);

    const [coaches, setCoaches] = useState([]);
    const [communicationLevels, setCommunicationLevels] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [enrichProgramStatuses, setEnrichProgramStatuses] = useState([]);
    const [ethnicities, setEthnicities] = useState([]);
    const [genders, setGenders] = useState([]);
    const [degreeTypes, setDegreeTypes] = useState([]);
    const [gpaMinimum, setGpaMinimum] = useState(0);
    const [graduationDates, setGraduationDates] = useState([]);
    const [hometowns, setHometowns] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [jobFunctions, setJobFunctions] = useState([]);
    const [majors, setMajors] = useState([]);
    const [preferredIndustries, setPreferredIndustries] = useState([]);
    const [resumeStatuses, setResumeStatuses] = useState([]);
    const [relocationPreferences, setRelocationPreferences] = useState([]);
    const [schools, setSchools] = useState([]);
    const [technicalSkills, setTechnicalSkills] = useState([]);

    const [resultColumns, setResultColumns] = useState(defaultResultColumns);
    const [sort, setSort] = useState();

    // adds job status columns to results table
    useEffect(() => {
        if (jobs.length > 0) {
            const candidacyColumns = jobs.map((job) => {
                let jobId = job.id;
                return {
                    property: 'candidacies',
                    header: <Text>{job.name} Status</Text>,
                    render: (contact) => (
                        <Text>
                            {
                                contact.candidacies.data.find(
                                    (c) => c.attributes.job.data.id == jobId,
                                )?.attributes.status
                            }
                        </Text>
                    ),
                };
            });
            setResultColumns(defaultResultColumns.concat(candidacyColumns));
        }
    }, [searchResults]);

    useEffect(() => {
        if (!resultsPage) return;
        handleSearch(resultsPage);
    }, [resultsPage, sort]);

    useEffect(() => {
        if (!searchJob) return;

        setGpaMinimum(searchJob.minimumGPA ?? 0);
        const selectedGradYears = searchJob.eligibleGradYears ?? [];
        setGraduationDates(
            graduationOptions.filter((g) => {
                return selectedGradYears.includes(
                    `${g.value.semester} ${g.value.year}`,
                );
            }),
        );
        setJobFunctions(searchJob.jobFunctions ?? []);
        setMajors(searchJob.idealMajors ?? []);
        setPreferredIndustries(searchJob.industries ?? []);

        const searchJobLocations = searchJob.job_locations?.map(
            (j) => j.location,
        );
        setRelocationPreferences(searchJobLocations ?? []);
        setTechnicalSkills(searchJob.relevantTechnicalSkills ?? []);
    }, [searchJob]);

    const handleSort = ({ property, direction }) => {
        setSort({ property, direction });
        setResultsPage(1);
    };

    const handleSearch = async () => {
        setButtonBusy(true);
        setSelectedContactIds([]);
        const filters = {
            coaches: coaches.map((obj) => obj.id),
            communicationLevels: communicationLevels.map((obj) => obj.id),
            companies: companies.map((obj) => obj.id),
            enrichProgramStatuses: enrichProgramStatuses.map((obj) => obj.name),
            ethnicities: ethnicities.map((obj) => obj.id),
            genders: genders.map((obj) => obj.name),
            degreeTypes: degreeTypes.map((obj) => obj.name),
            gpaMinimum: parseFloat(gpaMinimum),
            graduationDates: graduationDates,
            hometowns: hometowns.map((obj) => obj.name),
            jobs: jobs.map((obj) => obj.id),
            majors: majors.map((obj) => obj.id),
            preferredIndustries: preferredIndustries.map((obj) => obj.id),
            preferredJobFunctions: jobFunctions.map((obj) => obj.id),
            resumeStatuses: resumeStatuses.map((obj) => obj.name),
            relocationPreferences: relocationPreferences.map((obj) => obj.id),
            schools: schools.map((obj) => obj.id),
            technicalSkills: technicalSkills.map((obj) => obj.id),
        };

        const responseData = await contactsQuery({
            ...filters,
            page: resultsPage,
            pageSize: 100,
            sort:
                sort && SORT_FIELD_MAP[sort.property]
                    ? [
                          `${SORT_FIELD_MAP[sort.property]}:${sort.direction}`,
                      ]
                    : undefined,
        });
        setSearchResults(responseData.contacts);
        setSearchResultsTotal(responseData.pagination.total);
        setSearchFilters(filters);
        setButtonBusy(false);
    };

    return (
        <Page
            kind="full"
            gap="small"
            pad={{ top: '30px', bottom: '100px', horizontal: 'small' }}>
            {isToastVisible && toastMessage && (
                <Notification
                    toast
                    status="critical"
                    message={toastMessage}
                    onClose={() => setIsToastVisible(false)}
                />
            )}
            <PageContent>
                <Heading level="2">Student Search</Heading>
            </PageContent>

            <PageContent>
                <Box
                    background="enrich-green-light"
                    round="small"
                    pad="small"
                    gap="small">
                    <Text weight="bold">Search with Job Fields</Text>
                    <Box>
                        <ApiSearch
                            contentType="jobs"
                            name="jobs"
                            filters={{
                                status: {
                                    $in: ['preparing', 'open', 'demo'],
                                },
                            }}
                            populate={{
                                idealMajors: true,
                                industries: true,
                                jobFunctions: true,
                                job_locations: {
                                    populate: ['location'],
                                },
                                relevantTechnicalSkills: true,
                                company: { fields: ['id', 'name'] },
                            }}
                            updateFormData={(results) => {
                                setSearchJob(results[0]);
                            }}
                        />
                    </Box>
                </Box>

                <Grid columns={{ count: 3, size: 'medium' }}>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Coach"
                            options={loaderData.coaches}
                            updateFormData={setCoaches}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Enrich Status"
                            options={loaderData.enrichProgramStatuses}
                            updateFormData={setEnrichProgramStatuses}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Ethnicity"
                            options={loaderData.ethnicities}
                            updateFormData={setEthnicities}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            placeholder="Graduation Date"
                            options={graduationOptions}
                            updateFormData={setGraduationDates}
                            value={graduationDates}
                            // valueKey="value"
                        />
                    </Box>
                    <Box pad="small">
                        <TextInput
                            placeholder="GPA Minimum"
                            value={gpaMinimum}
                            onChange={(event) =>
                                setGpaMinimum(event.target.value)
                            }
                            size="xsmall"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Gender"
                            options={loaderData.genders}
                            updateFormData={setGenders}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Degree Type"
                            options={loaderData.degreeTypes}
                            updateFormData={setDegreeTypes}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Industry Preferences"
                            options={loaderData.industries}
                            updateFormData={setPreferredIndustries}
                            value={preferredIndustries}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Hometown"
                            options={loaderData.locations}
                            updateFormData={setHometowns}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Major"
                            options={loaderData.majors}
                            updateFormData={setMajors}
                            value={majors}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey={(opt) => {
                                return (
                                    <Text size="small">
                                        {opt.name} <b>[{opt.companyName}]</b>
                                    </Text>
                                );
                            }}
                            placeholder="Matched with job"
                            options={loaderData.jobs}
                            updateFormData={setJobs}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Matched with company"
                            options={loaderData.companies}
                            updateFormData={setCompanies}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="label"
                            placeholder="Resume status"
                            options={resumeStatusOptions}
                            updateFormData={setResumeStatuses}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <ApiOptionsSelect
                            contentType="locations"
                            name="relocationPreferences"
                            placeholder="Relocation Preferences"
                            updateFormData={(v) => setRelocationPreferences(v)}
                            value={relocationPreferences.map((r) => {
                                const state = r.state ?? '?';
                                return {
                                    name: `${r.name}, ${state}`,
                                    value: r,
                                };
                            })}
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Schools"
                            options={loaderData.schools}
                            updateFormData={setSchools}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <ApiOptionsSelect
                            contentType="technical-skills"
                            name="technicalSkills"
                            placeholder="Technical Skills"
                            updateFormData={(v) => setTechnicalSkills(v)}
                            value={technicalSkills.map((t) => {
                                return {
                                    name: t.name,
                                    value: t,
                                };
                            })}
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Preferred Job Functions"
                            options={loaderData.jobFunctions}
                            updateFormData={setJobFunctions}
                            value={jobFunctions}
                            valueKey="id"
                        />
                    </Box>
                    <Box pad="small">
                        <FilterOptionSelect
                            labelKey="name"
                            placeholder="Communication Level"
                            options={AppConstants.communicationLevels}
                            updateFormData={setCommunicationLevels}
                            valueKey="id"
                        />
                    </Box>
                </Grid>
                <Box margin={{ top: 'small' }}>
                    <Button
                        primary
                        busy={buttonBusy}
                        label="Search"
                        size="large"
                        onClick={() => {
                            setResultsPage(1);
                            handleSearch();
                        }}
                    />
                </Box>
            </PageContent>

            <PageContent>
                <Box
                    direction="row-responsive"
                    justify="between"
                    align="center">
                    <Heading level="3">
                        Search Results{' '}
                        {searchResultsTotal && (
                            <span>({searchResultsTotal})</span>
                        )}
                    </Heading>
                    <Box direction="row-responsive" gap="small">
                        <NewMatchButton
                            isMatchForSingleContact={false}
                            contactIds={selectedContactIds}
                            revalidate={revalidator.revalidate}
                            setIsToastVisible={setIsToastVisible}
                            setToastMessage={setToastMessage}
                        />

                        <ContactListButton options={searchFilters} />
                    </Box>
                </Box>
                <Box gap="medium">
                    <Box>
                        <DataTable
                            data={searchResults}
                            columns={resultColumns}
                            primaryKey="id"
                            select={selectedContactIds}
                            onSelect={setSelectedContactIds}
                            sort={sort}
                            onSort={handleSort}
                        />
                    </Box>
                    <Box align="center" justify="center" pad="small">
                        <Pagination
                            numberItems={searchResultsTotal}
                            onChange={({ page }) => setResultsPage(page)}
                            step={100}
                        />
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

const SORT_FIELD_MAP = {
    firstName: 'firstName',
    graduationYear: 'graduationYear',
    'school.data.attributes.name': 'school.name',
    email: 'email',
    communicationLevel: 'communicationLevel',
    resumeStatus: 'resumeStatus',
};

const filterFieldsQuery = async () => {
    const query = `query myQuery {
        getFilterFields {
            coaches {
                id
                name
            }
            companies {
                id
                name
            }
            enrichProgramStatuses {
                id
                name
            }
            ethnicities {
                id
                name
            }
            degreeTypes {
                id
                name
            }
            genders {
                id
                name
            }
            graduationSemesters {
                id
                name
            }
            industries {
                id
                name
            }
            jobs {
                id
                name
                companyName
            }
            locations {
                id
                name
            }
            majors {
                id
                name
            }
            resumeStatuses {
                id
                name
            }
            schools {
                id
                name
            }
            jobFunctions {
                id
                name
            }
        }
    }`;

    try {
        const graphqlResponse = await API.post('graphql', { query });
        return graphqlResponse.data.data.getFilterFields;
    } catch (error) {
        console.log(error);
        return [];
    }
};

export async function loader() {
    let filterFields;
    try {
        filterFields = await filterFieldsQuery();
    } catch (error) {
        console.log(error);
        return [];
    }

    return filterFields;
}

export function action() {}
